import { Box, Typography } from '@mui/material';
import { ArrowDown, ArrowRight, Droplet, Package, type LucideIcon } from 'lucide-react';
import { Fragment, type ReactElement, type ReactNode } from 'react';
import { Link } from 'react-router';

import { dataRecordPath } from '@/admin/data/routes';
import { Badge, Card, Icon } from '@/ui/kit';
import { fontStacks } from '@/ui/tokens';

import { stockSearchPath } from './routes';
import { batchCodes, describeStatus, formatMoment, lineageGenerations, shortId } from './stockPresentation';
import type { StockBatch, StockLineage, StockLineageNode } from './stockTypes';

/**
 * Genealogie d'un lot : de l'origine (le tirage) a la descendance, en passant par
 * le lot courant, surligne.
 *
 * Les generations sont des colonnes de gauche a droite — empilees sous 900 px —,
 * et chaque colonne est un element d'une liste ordonnee : l'ordre se lit sans la
 * fleche, qui n'est que decorative. Un lot de l'arbre est un lien vers sa propre
 * trace, et le tirage d'origine un lien vers son enregistrement dans l'explorateur.
 *
 * **Aucun lien vers un biberon** : le serveur ne rend pas l'identifiant de la
 * seance d'alimentation qui a consomme un lot. Un lot consomme porte donc
 * seulement « Consommation ».
 */

interface NodeCardProps {
  readonly icon: LucideIcon;
  readonly title: ReactNode;
  readonly lines?: readonly string[];
  readonly status?: string | null;
  readonly current?: boolean;
  readonly footer?: ReactNode;
}

const NodeCard = ({ icon, title, lines = [], status = null, current = false, footer }: NodeCardProps): ReactElement => {
  const badge = describeStatus(status);

  return (
    <Box
      component="li"
      aria-current={current ? 'true' : undefined}
      sx={{
        width: { xs: '100%', md: 230 },
        boxSizing: 'border-box',
        p: 1.75,
        borderRadius: '14px',
        border: current ? 2 : 1,
        borderColor: current ? 'tokens.primary' : 'tokens.border',
        bgcolor: current ? 'tokens.primarySoft' : 'tokens.surface',
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box component="span" sx={{ display: 'inline-flex', color: current ? 'tokens.primary' : 'tokens.textMuted' }}>
          <Icon icon={icon} size={16} />
        </Box>
        <Typography component="div" sx={{ fontSize: 13, fontWeight: 700, minWidth: 0, overflowWrap: 'anywhere' }}>
          {title}
        </Typography>
      </Box>
      {badge === null ? null : (
        <Box>
          <Badge label={badge.label} tone={badge.tone} />
        </Box>
      )}
      {lines.map((line) => (
        <Typography key={line} sx={{ fontSize: 12, color: 'tokens.textMuted', overflowWrap: 'anywhere' }}>
          {line}
        </Typography>
      ))}
      {footer === undefined ? null : (
        <Typography component="div" sx={{ fontSize: 12 }}>
          {footer}
        </Typography>
      )}
    </Box>
  );
};

/** Lien d'un noeud : la trace d'un autre lot (cet ecran), ou un enregistrement de l'explorateur. */
const NodeLink = ({ to, children, mono = false }: { to: string; children: ReactNode; mono?: boolean }): ReactElement => (
  <Box
    component={Link}
    to={to}
    sx={{
      color: 'inherit',
      fontFamily: mono ? fontStacks.mono : undefined,
      textUnderlineOffset: 3,
      '&:focus-visible': { outline: '2px solid', outlineColor: 'tokens.primary', outlineOffset: 2, borderRadius: '4px' },
    }}
  >
    {children}
  </Box>
);

const lotLink = (id: string, code: string | null): ReactElement => (
  <NodeLink to={stockSearchPath(id)} mono>
    {code ?? `Lot ${shortId(id)}`}
  </NodeLink>
);

/** Ce que le lien dit du rattachement, du point de vue du lot trace. */
const relationLines = (
  node: StockLineageNode,
  side: 'parent' | 'child',
  codes: ReadonlyMap<string, string | null>,
): readonly string[] => {
  const target = codes.get(node.linkedTo);
  const targetName = target === undefined ? `lot ${shortId(node.linkedTo)}` : (target ?? `lot ${shortId(node.linkedTo)}`);

  const relation =
    side === 'parent'
      ? node.link === 'split'
        ? `Réparti en ${targetName}`
        : `Fusionné dans ${targetName}`
      : node.link === 'split'
        ? `Issu d’une répartition de ${targetName}`
        : `Issu d’une fusion incluant ${targetName}`;

  return [relation, ...(node.status === 'consumed' ? ['Consommation'] : [])];
};

const Generation = ({ last, children }: { last: boolean; children: ReactNode }): ReactElement => (
  <Box
    component="li"
    sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'center', gap: 1, flexShrink: 0 }}
  >
    <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 1.5, width: { xs: '100%', md: 'auto' } }}>
      {children}
    </Box>
    {last ? null : (
      <Box component="span" aria-hidden sx={{ display: 'inline-flex', color: 'tokens.textMuted' }}>
        <Box component="span" sx={{ display: { xs: 'none', md: 'inline-flex' } }}>
          <Icon icon={ArrowRight} size={20} />
        </Box>
        <Box component="span" sx={{ display: { xs: 'inline-flex', md: 'none' } }}>
          <Icon icon={ArrowDown} size={20} />
        </Box>
      </Box>
    )}
  </Box>
);

export const BatchLineage = ({
  batch,
  lineage,
  scopeId,
}: {
  batch: StockBatch;
  lineage: StockLineage;
  /** Périmètre du journal du lot, celui que la recherche a rendu. */
  scopeId: string;
}): ReactElement => {
  const codes = batchCodes(batch, lineage);
  const generations = lineageGenerations(lineage);
  const alone = lineage.parents.length === 0 && lineage.children.length === 0 && lineage.pumpingSession === null;

  // Les colonnes, dans l'ordre de lecture ; la derniere ne porte pas de fleche.
  const columns: ReactElement[] = [];

  if (lineage.pumpingSession !== null) {
    const session = lineage.pumpingSession;

    columns.push(
      <NodeCard
        key="pumping"
        icon={Droplet}
        title="Tirage"
        lines={[session.pumpedAt === null ? 'Date de la séance illisible' : formatMoment(session.pumpedAt)]}
        footer={<NodeLink to={dataRecordPath(scopeId, 'pumping_session', session.id)}>Ouvrir dans l’explorateur</NodeLink>}
      />,
    );
  }

  generations.parents.forEach((group, index) => {
    columns.push(
      <Fragment key={`parents-${String(index)}`}>
        {group.map((node) => (
          <NodeCard
            key={node.batchId}
            icon={Package}
            title={lotLink(node.batchId, node.code)}
            status={node.status}
            lines={relationLines(node, 'parent', codes)}
          />
        ))}
      </Fragment>,
    );
  });

  columns.push(
    <NodeCard
      key="current"
      icon={Package}
      title={<Box component="span" sx={{ fontFamily: fontStacks.mono }}>{batch.code ?? `Lot ${shortId(batch.id)}`}</Box>}
      status={batch.status}
      lines={['Ce lot']}
      current
    />,
  );

  generations.children.forEach((group, index) => {
    columns.push(
      <Fragment key={`children-${String(index)}`}>
        {group.map((node) => (
          <NodeCard
            key={node.batchId}
            icon={Package}
            title={lotLink(node.batchId, node.code)}
            status={node.status}
            lines={relationLines(node, 'child', codes)}
          />
        ))}
      </Fragment>,
    );
  });

  return (
    <Card
      title="Généalogie"
      subtitle="Origine et descendance du lot, recalculées depuis les mouvements du journal."
    >
      <Box
        role="region"
        aria-label="Arbre de généalogie du lot"
        tabIndex={0}
        sx={{
          overflowX: 'auto',
          py: 0.5,
          '&:focus-visible': { outline: '2px solid', outlineColor: 'tokens.primary', outlineOffset: 2, borderRadius: '8px' },
        }}
      >
        <Box
          component="ol"
          sx={{
            listStyle: 'none',
            m: 0,
            p: 0,
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'stretch', md: 'center' },
            gap: 1,
            width: 'max-content',
            maxWidth: { xs: '100%', md: 'none' },
            minWidth: { xs: 0, md: '100%' },
          }}
        >
          {columns.map((column, index) => (
            <Generation key={column.key} last={index === columns.length - 1}>
              {column}
            </Generation>
          ))}
        </Box>
      </Box>

      {alone ? (
        <Typography sx={{ fontSize: 13, color: 'tokens.textMuted' }}>
          Le journal ne montre ni tirage d’origine, ni parent, ni descendant pour ce lot.
        </Typography>
      ) : null}

      {lineage.truncated ? (
        <Typography role="note" sx={{ fontSize: 13, color: 'tokens.textMuted' }}>
          L’arbre est tronqué : dix générations et cinquante lots au plus sont affichés de chaque côté.
        </Typography>
      ) : null}
    </Card>
  );
};
